import React from 'react';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

import Login from './Login';
import Home from './Home';
import ProjectPage from './ProjectPage';
import SurveyDashboard from './SurveyDashboard';
import AddProjectForm from './AddProject';
import AddSurveyForm from './AddSurvey';

import DuplicateRemoval from './DuplicateRemoval';
import TextAnalysisHome from './TextAnalysisHome';
import GibberishDetection from './GibberishDetection';
import AIDetection from './AIDetection';
import DisqualIDs from './DisqualifyIDspage';
import SurveyPace from './SurveyPace';
import SpeederCheck from './SpeederCheck';

import QuotaMonitoring from './QuotaMonitoring';
import AddQuotaViewForm from './MakeQuotaView';
import AddQuotasHome from './AddQuotasHome';
import UKNatRepQuotas from './UKNatRepQuotas';
import ManualQuotas from './ManualQuotas';
import BulkQuotas from './BulkQuotas';
import FilteredQuotaPage from './FilteredQuotaPage';
import PartnerViewsPage from './sharing/PartnerViewsPage';
import PartnersPage from './sharing/PartnersPage';
import SetPasswordPage from './sharing/SetPasswordPage';
import SharedQuotaPage from './sharing/SharedQuotaPage';
import QuotaTracker from './QuotaTracker';
import QuotaTrackerDetail from './QuotaTrackerDetail';
import UserSettings from './UserSettings';
import SurveyScripting from './SurveyScripting';
import SurveyTesting from './SurveyTesting';
import WranglerSetup from './wrangler/WranglerSetup';
import WranglerPage from './wrangler/WranglerPage';

const Stack = ({ children }) => (
  <ProtectedRoute requiredRole="stack">{children}</ProtectedRoute>
);

const Auth = ({ children }) => (
  <ProtectedRoute>{children}</ProtectedRoute>
);

// Partners land on the list of views shared with them; everyone else on Home.
const Landing = () => {
  const { user } = useAuth();
  return user?.role === 'external' ? <Navigate to="/quota-monitoring" replace /> : <Home />;
};

const App = () => (
  <AuthProvider>
    <BrowserRouter>
      <ErrorBoundary>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        {/* Opened by link, with no login (7k): one quota view, or setting a password. */}
        <Route path="/s/:token" element={<SharedQuotaPage />} />
        <Route path="/set-password/:uid/:token" element={<SetPasswordPage />} />

        {/* All authenticated users */}
        <Route path="/" element={<Auth><Landing /></Auth>} />
        <Route path="/project/:projectId" element={<Auth><ProjectPage /></Auth>} />
        <Route path="/monitor-quotas/:quotaMonitorId" element={<Auth><FilteredQuotaPage /></Auth>} />
        <Route path="/quota-monitoring" element={<Auth><PartnerViewsPage /></Auth>} />

        {/* Stack only — survey tools */}
        <Route path="/survey/:surveyId" element={<Stack><SurveyDashboard /></Stack>} />
        <Route path="/survey/:surveyId/duplicate-removal" element={<Stack><DuplicateRemoval /></Stack>} />
        <Route path="/survey/:surveyId/text-analysis" element={<Stack><TextAnalysisHome /></Stack>} />
        <Route path="/survey/:surveyId/text-analysis/gibberish" element={<Stack><GibberishDetection /></Stack>} />
        <Route path="/survey/:surveyId/text-analysis/ai" element={<Stack><AIDetection /></Stack>} />
        <Route path="/survey/:surveyId/disqualify-ids" element={<Stack><DisqualIDs /></Stack>} />
        <Route path="/survey/:surveyId/survey-pace" element={<Stack><SurveyPace /></Stack>} />
        <Route path="/survey/:surveyId/speeder-check" element={<Stack><SpeederCheck /></Stack>} />
        <Route path="/survey/:surveyId/quota-monitoring" element={<Stack><QuotaMonitoring /></Stack>} />
        <Route path="/survey/:surveyId/make-quota-view" element={<Stack><AddQuotaViewForm /></Stack>} />
        <Route path="/survey/:surveyId/add-quotas" element={<Stack><AddQuotasHome /></Stack>} />
        <Route path="/survey/:surveyId/add-quotas/uk-nat-rep" element={<Stack><UKNatRepQuotas /></Stack>} />
        <Route path="/survey/:surveyId/add-quotas/manual" element={<Stack><ManualQuotas /></Stack>} />
        <Route path="/survey/:surveyId/add-quotas/bulk" element={<Stack><BulkQuotas /></Stack>} />
        <Route path="/survey/:surveyId/quota-tracker" element={<Stack><QuotaTracker /></Stack>} />
        <Route path="/survey/:surveyId/quota-tracker/:trackerId" element={<Stack><QuotaTrackerDetail /></Stack>} />
        <Route path="/survey/:surveyId/scripting" element={<Stack><SurveyScripting /></Stack>} />
        <Route path="/survey/:surveyId/survey-testing" element={<Stack><SurveyTesting /></Stack>} />
        <Route path="/survey/:surveyId/wrangler" element={<Stack><WranglerPage /></Stack>} />
        <Route path="/survey/:surveyId/wrangler-setup" element={<Stack><WranglerSetup /></Stack>} />

        {/* Stack only — project/survey management */}
        <Route path="/add-project" element={<Stack><AddProjectForm /></Stack>} />
        <Route path="/project/:projectId/add-survey" element={<Stack><AddSurveyForm /></Stack>} />
        <Route path="/settings" element={<Stack><UserSettings /></Stack>} />
        <Route path="/partners" element={<Stack><PartnersPage /></Stack>} />
      </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  </AuthProvider>
);

export default App;
